/**
 * Авторские индикаторы потока внутри свечей: кластеры, перекос, крупные объёмы.
 *
 * Три отдельных индикатора, а не режимы одного: человек включает то, что
 * ему нужно сейчас, и не ищет нужное в выпадающем списке. Данные у них
 * общие — сделки Binance и Bybit по уровням, — и грузятся один раз, сколько
 * бы из них ни стояло на графике.
 */

import { barsSettings, type FlowBarsView } from '../orderflow/bars-settings.js';
import type { IndicatorDefinition, IndicatorParamSpec, IndicatorParams, IndicatorContext } from './types.js';
import type { IndicatorShape } from './overlays.js';
import { TABLE_PARAM } from './explain.js';
import { explainClusters, explainImbalance, explainLarge } from './explain-orderflow.js';

const SOURCE: IndicatorParamSpec = {
  key: 'source',
  label: 'Биржи',
  hint: 'Обе — весь объём рынка. Одна — если хочется понять, где именно торговали.',
  type: 'select',
  default: 'all',
  options: [
    { value: 'all', label: 'Binance и Bybit' },
    { value: 'binance', label: 'Только Binance' },
    { value: 'bybit', label: 'Только Bybit' },
  ],
};

const UNIT: IndicatorParamSpec = {
  key: 'unit',
  label: 'Считать в',
  hint: 'В чём показывать объём в карточке уровня: в долларах или в монетах.',
  type: 'select',
  default: 'usd',
  options: [
    { value: 'usd', label: 'Долларах' },
    { value: 'coin', label: 'Монетах' },
  ],
};

const RATIO: IndicatorParamSpec = {
  key: 'ratio',
  label: 'Перекос от, %',
  hint:
    'Во сколько раз одна сторона должна быть сильнее другой. 300% — втрое: покупатели съели в три раза больше, чем продавцы уровнем ниже. Меньше — отметок больше, но среди них больше шума.',
  type: 'number',
  default: 300,
  min: 150,
  max: 1000,
  step: 50,
};

const MIN_VOLUME: IndicatorParamSpec = {
  key: 'minVolume',
  label: 'Не меньше, $',
  hint:
    'Сколько долларов должно пройти у сильной стороны, чтобы перекос считался. «Сто против нуля» на сотне долларов — случайность, а не давление.',
  type: 'number',
  default: 0,
  min: 0,
  max: 10_000_000,
  step: 1000,
};

const BUY_COLOR: IndicatorParamSpec = {
  key: 'buyColor',
  label: 'Цвет покупок',
  hint: 'Каким цветом рисовать перевес и перекос покупателей.',
  type: 'color',
  default: '#16C784',
};

const SELL_COLOR: IndicatorParamSpec = {
  key: 'sellColor',
  label: 'Цвет продаж',
  hint: 'Каким цветом рисовать перевес и перекос продавцов.',
  type: 'color',
  default: '#EA3943',
};

function shapesFor(view: FlowBarsView) {
  return (_candles: unknown, p: IndicatorParams, context: IndicatorContext): readonly IndicatorShape[] => {
    const data = context.orderFlow;
    if (!data) return [];
    return [{ kind: 'flow-bars', data, settings: barsSettings(p, view), key: `${view}|${JSON.stringify(p)}` }];
  };
}

export const ORDER_FLOW_CLUSTERS: IndicatorDefinition = {
  id: 'order-flow-clusters',
  name: 'Кластеры',
  description:
    'Что происходило внутри каждой свечи: сколько продали и купили на каждой цене. ' +
    'Приблизьте график — в клетках появятся числа.',
  author: 'specter',
  placement: 'overlay',
  orderFlow: true,
  params: [
    {
      key: 'content',
      label: 'Что в клетке',
      hint:
        'Продажи × покупки — обе стороны уровня, как в кластерных терминалах. Перевес — одно число: плюс, если покупали больше. Объём — сколько всего прошло на цене.',
      type: 'select',
      default: 'bidask',
      options: [
        { value: 'bidask', label: 'Продажи × покупки' },
        { value: 'delta', label: 'Перевес' },
        { value: 'volume', label: 'Объём' },
      ],
    },
    SOURCE,
    UNIT,
    {
      key: 'highlight',
      label: 'Подсвечивать перекос',
      hint: 'Полоска у клетки, где одна сторона задавила другую: справа — покупатели, слева — продавцы.',
      type: 'boolean',
      default: true,
    },
    RATIO,
    MIN_VOLUME,
    {
      key: 'poc',
      label: 'Главный уровень свечи',
      hint: 'Рамка вокруг цены, где внутри свечи торговали больше всего.',
      type: 'boolean',
      default: true,
    },
    BUY_COLOR,
    SELL_COLOR,
    {
      key: 'volumeColor',
      label: 'Цвет объёма',
      hint: 'Цвет клеток, когда в них показывается объём.',
      type: 'color',
      default: '#7C8DB5',
    },
    {
      key: 'pocColor',
      label: 'Цвет главного уровня',
      hint: 'Цвет рамки вокруг цены, где внутри свечи наторговали больше всего: её находят взглядом первой.',
      type: 'color',
      default: '#F5A623',
    },
    TABLE_PARAM,
  ],
  outputs: [],
  warmup: () => 0,
  calc: () => ({}),
  shapes: shapesFor('clusters'),
  readout: (candles, p, context) =>
    explainClusters(candles, context.orderFlow, barsSettings(p, 'clusters'), context.livePartial),
  label: () => 'Кластеры',
};

export const ORDER_FLOW_IMBALANCE: IndicatorDefinition = {
  id: 'order-flow-imbalance',
  name: 'Перекос покупок и продаж',
  description:
    'Где одна сторона давила сильнее другой в разы. Несколько таких уровней подряд — зона, ' +
    'к которой цена часто возвращается как к опоре или сопротивлению.',
  author: 'specter',
  placement: 'overlay',
  orderFlow: true,
  params: [
    SOURCE,
    UNIT,
    RATIO,
    MIN_VOLUME,
    {
      key: 'singles',
      label: 'Отдельные уровни',
      hint: 'Чёрточка у свечи на каждом уровне с перекосом: справа — покупатели, слева — продавцы.',
      type: 'boolean',
      default: true,
    },
    {
      key: 'zones',
      label: 'Зоны от стопок',
      hint: 'Когда перекос идёт несколько уровней подряд, от свечи вправо тянется зона — видно, вернулась ли к ней цена.',
      type: 'boolean',
      default: true,
    },
    {
      key: 'stack',
      label: 'Стопка — уровней подряд',
      hint: 'Сколько уровней с перекосом подряд нужно, чтобы нарисовать зону. Три — обычное значение.',
      type: 'number',
      default: 3,
      min: 2,
      max: 10,
      step: 1,
    },
    {
      key: 'zoneBars',
      label: 'Длина зоны, свечей',
      hint: 'Насколько далеко вправо тянуть зону от свечи, где она появилась.',
      type: 'number',
      default: 30,
      min: 1,
      max: 500,
      step: 1,
    },
    BUY_COLOR,
    SELL_COLOR,
    TABLE_PARAM,
  ],
  outputs: [],
  warmup: () => 0,
  calc: () => ({}),
  shapes: shapesFor('imbalance'),
  readout: (candles, p, context) =>
    explainImbalance(candles, context.orderFlow, barsSettings(p, 'imbalance'), context.livePartial),
  label: (p) => `Перекос ×${(barsSettings(p, 'imbalance').ratio).toFixed(1).replace('.', ',')}`,
};

export const ORDER_FLOW_LARGE: IndicatorDefinition = {
  id: 'order-flow-large',
  name: 'Крупные объёмы',
  description:
    'Пузыри на ценах, где внутри свечи наторговали в разы больше обычного. ' +
    'Зелёный — покупали больше, красный — продавали. Чем крупнее пузырь, тем сильнее выброс.',
  author: 'specter',
  placement: 'overlay',
  orderFlow: true,
  params: [
    SOURCE,
    UNIT,
    {
      key: 'large',
      label: 'Крупнее обычного, раз',
      hint:
        'Обычный — медиана объёма уровня у свечей на экране. 2 — повышенный, 3 — крупный, 5 — экстремальный. Меньше число — пузырей больше.',
      type: 'number',
      default: 3,
      min: 1.5,
      max: 20,
      step: 0.5,
    },
    BUY_COLOR,
    SELL_COLOR,
    TABLE_PARAM,
  ],
  outputs: [],
  warmup: () => 0,
  calc: () => ({}),
  shapes: shapesFor('large'),
  readout: (candles, p, context) =>
    explainLarge(candles, context.orderFlow, barsSettings(p, 'large'), context.livePartial),
  label: (p) => `Крупные объёмы ×${barsSettings(p, 'large').large.toString().replace('.', ',')}`,
};
